<template>
    <section class="group-section">
        <header class="section-header">
            <h1 class="section-title">Grupy lawet</h1>
            <button class="btn btn-submit" @click="openForm()">
                <font-awesome-icon icon="plus" /> Dodaj
            </button>
        </header>

        <EntityFilterBar v-model:search="searchQuery" v-model:sort="sortModel" v-model:filters="filters"
            :filters-schema="filtersSchema" :sort-options="sortOptions" />

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <div class="status-wrapper">
            <p v-if="trailerStore.trailerGroupsLoading" class="status-loading">Ładowanie...</p>
        </div>

        <div v-if="!trailerStore.trailerGroupsLoading && !trailerStore.error">
            <div v-if="filteredGroups.length" class="group-list">
                <EntityCard v-for="group in paginatedGroups" :key="group.id">
                    <template #content>
                        <div class="group-meta">
                            <h3 class="group-name">{{ group.name }}</h3>
                            <p class="group-material"><strong>Materiał:</strong> {{ group.material }}</p>
                            <p class="group-description" v-if="group.description">
                                <strong>Opis:</strong> {{ group.description }}
                            </p>
                        </div>
                    </template>

                    <template #actions>
                        <button class="btn btn-edit" @click="editGroup(group)">
                            <font-awesome-icon icon="pen" /> Edytuj
                        </button>
                        <button class="btn btn-delete" @click="confirmDelete(group.id)">
                            <font-awesome-icon icon="trash" /> Usuń
                        </button>
                    </template>
                </EntityCard>
            </div>

            <div v-else class="empty-state">
                <font-awesome-icon icon="layer-group" size="2x" />
                <p>Brak grup lawet do wyświetlenia</p>
            </div>
        </div>

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <teleport to="body">
            <transition name="fade">
                <TrailerGroupForm v-if="showForm" :visible="showForm" :group="editingGroup" @close="showForm = false"
                    @saved="trailerStore.fetchTrailerGroups" />
            </transition>
        </teleport>

        <teleport to="body">
            <ConfirmDialog :visible="confirmDialog.state.isVisible" :title="confirmDialog.state.dialogOptions.title"
                :message="confirmDialog.state.dialogOptions.message"
                :confirmText="confirmDialog.state.dialogOptions.confirmText"
                :cancelText="confirmDialog.state.dialogOptions.cancelText"
                :danger="confirmDialog.state.dialogOptions.danger" @confirm="confirmDialog.handleConfirm"
                @cancel="confirmDialog.handleCancel" @close="confirmDialog.handleClose" />
        </teleport>
    </section>
</template>

<script>
import { useTrailerStore } from '@/stores/trailerStore'
import { showErrorToast, showSuccessToast } from '@/utils/errorUtils'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import EntityCard from '@/components/cms/EntityCard.vue'
import EntityFilterBar from '@/components/cms/EntityFilterBar.vue'
import TrailerGroupForm from './TrailerGroupForm.vue'
import PaginationControls from '@/components/cms/PaginationControls.vue'
import ConfirmDialog from '@/components/cms/ConfirmDialog.vue'

export default {
    components: {
        EntityCard,
        EntityFilterBar,
        TrailerGroupForm,
        PaginationControls,
        ConfirmDialog
    },
    setup() {
        const confirmDialog = useConfirmDialog()
        return { confirmDialog }
    },
    data() {
        return {
            trailerStore: useTrailerStore(),
            showForm: false,
            editingGroup: null,
            searchQuery: '',
            sortModel: { by: '', order: 'asc' },
            filters: {
                material: ''
            },
            currentPage: 1,
            itemsPerPage: 50
        }
    },
    computed: {
        filtersSchema() {
            const materials = [...new Set(this.trailerStore.trailerGroups.map(g => g.material).filter(Boolean))]
            return [
                {
                    key: 'material',
                    label: 'Materiał',
                    type: 'select',
                    options: materials
                }
            ]
        },
        sortOptions() {
            return [
                { key: 'name', label: 'Nazwa' },
                { key: 'material', label: 'Materiał' }
            ]
        },
        filteredGroups() {
            let groups = this.trailerStore.trailerGroups

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase()
                groups = groups.filter(group => group.name?.toLowerCase().includes(query))
            }

            if (this.filters.material) {
                groups = groups.filter(group => group.material === this.filters.material)
            }

            if (this.sortModel.by) {
                groups = [...groups].sort((a, b) => {
                    const aVal = a[this.sortModel.by]
                    const bVal = b[this.sortModel.by]

                    if (aVal < bVal) return this.sortModel.order === 'asc' ? -1 : 1
                    if (aVal > bVal) return this.sortModel.order === 'asc' ? 1 : -1
                    return 0
                })
            }

            return groups
        },
        paginatedGroups() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            return this.filteredGroups.slice(start, start + this.itemsPerPage)
        },
        totalPages() {
            return Math.ceil(this.filteredGroups.length / this.itemsPerPage)
        }
    },
    mounted() {
        if (this.trailerStore.trailerGroups.length === 0) {
            this.trailerStore.fetchTrailerGroups()
        }
    },
    methods: {
        openForm(group = null) {
            this.editingGroup = group
            this.showForm = true
        },
        editGroup(group) {
            this.openForm(group)
        },
        async confirmDelete(id) {
            const confirmed = await this.confirmDialog.showConfirm({
                title: 'Usunąć grupę?',
                message: 'Ta operacja jest nieodwracalna. Wszystkie dane związane z tą grupą zostaną usunięte.',
                confirmText: 'Tak, usuń',
                cancelText: 'Anuluj',
                danger: true
            })

            if (confirmed) {
                this.trailerStore.loading = true
                try {
                    await this.trailerStore.deleteTrailerGroup(id)
                    showSuccessToast('Usunięto grupę.')
                } catch (err) {
                    showErrorToast(err, 'Błąd przy usuwaniu grupy.')
                } finally {
                    this.trailerStore.loading = false
                }
            }
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        }
    }
}
</script>

<style scoped lang="scss">
.group-section {
    max-width: 1200px;
    margin: 0 auto;
    cursor: default;
}

.section-header {
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $color-primary-dark;
    margin: 0;
}

.status-wrapper {
    margin-bottom: 1.5rem;
}

.status-loading {
    font-size: 1rem;
    color: $color-text-light;
    text-align: center;
}

.group-list {
    display: grid;
    gap: 1.25rem;
}

.group-meta {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.group-name {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
}

.group-material,
.group-description {
    font-size: 0.95rem;
    color: $color-text-light;
}

.group-description {
    margin: 0;
}

.group-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &-edit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }
    }

    &-delete {
        background: $color-grey-light;
        color: $color-primary-accent;

        &:hover {
            background: $color-primary-accent;
            color: white;
        }
    }

    &-submit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    border: 1px solid $color-grey;

    svg {
        color: $color-grey-dark;
    }

    p {
        color: $color-text-light;
        font-size: 1.1rem;
        margin: 0;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
